import { useEffect, useState, useRef } from "react";
import { Link, useNavigate, Routes, Route, useParams } from "react-router-dom";
import {
  Plus,
  ClipboardList,
  LifeBuoy,
  ArrowRight,
  Image as ImageIcon,
  Mic,
  Video as VideoIcon,
  Paperclip,
  CheckCircle2,
  Send,
  AlertOctagon,
  Square,
  ArrowLeft,
} from "lucide-react";
import api, { formatErr } from "../lib/api";
import Modal from "../components/Modal";
import JobCardRow from "../components/JobCardRow";
import { uploadFile, uploadUrl } from "../lib/uploads";
import { useAuth } from "../context/AuthContext";
import { useBranding } from "../context/TenantBrandingContext";
import CustomerProfileDocumentsBlock from "../components/customer-blocks/CustomerProfileDocumentsBlock";
import VoiceTextarea from "../components/VoiceTextarea";
import PortalTeamManager from "../components/portal/PortalTeamManager";
import PerformanceBlock from "../components/perf/PerformanceBlock";
import PortalRouteGuard from "../components/portal/PortalRouteGuard";

/* ====================== Overview ====================== */
function PortalOverview() {
  const [jobs, setJobs] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [openNew, setOpenNew] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    const [j, t] = await Promise.all([api.get("/jobcards"), api.get("/tickets")]);
    setJobs(j.data);
    setTickets(t.data);
  };
  useEffect(() => {
    load();
  }, []);

  const openTickets = tickets.filter((t) => !["closed", "resolved", "resolved_virtually", "resolved_external", "resolved_via_jc", "cancelled"].includes(t.status));
  const activeJobs = jobs.filter((j) => j.phase !== "paid" && j.phase !== "signed_off");

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-[#647087]">Hello again</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-1">
            Your service hub
          </h1>
        </div>
        <button
          onClick={() => setOpenNew(true)}
          data-testid="portal-new-ticket-btn"
          className="hv-btn hv-btn-primary"
        >
          <Plus className="w-4 h-4" /> Log a new ticket
        </button>
      </div>

      <div className="grid sm:grid-cols-3 gap-3">
        <Kpi label="My active jobs" value={activeJobs.length} accent="#A78BFA" icon={ClipboardList} />
        <Kpi label="My open tickets" value={openTickets.length} accent="#F43F5E" icon={LifeBuoy} />
        <Kpi label="Completed jobs" value={jobs.length - activeJobs.length} accent="#10B981" icon={CheckCircle2} />
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="hv-card p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-display text-xl font-bold flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-[#A78BFA] animate-pulse" /> My jobs
            </h3>
            <Link
              to="/portal/jobs"
              data-testid="portal-jobs-link"
              className="text-xs text-[#A78BFA] hover:underline"
            >
              See all
            </Link>
          </div>
          <div className="space-y-2">
            {jobs.length === 0 && (
              <div className="text-sm text-[#647087]">No jobs scheduled yet.</div>
            )}
            {jobs.slice(0, 5).map((j) => (
              <JobCardRow
                key={j.id}
                job={j}
                to={`/portal/jobcards/${j.id}`}
                showProgress={false}
                accent="#A78BFA"
                testIdPrefix="portal-job"
              />
            ))}
          </div>
        </div>

        <div className="hv-card p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-display text-xl font-bold flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-rose-400 animate-pulse" /> My tickets
            </h3>
            <Link
              to="/portal/tickets"
              data-testid="portal-tickets-link"
              className="text-xs text-rose-300 hover:underline"
            >
              See all
            </Link>
          </div>
          <div className="space-y-2">
            {tickets.length === 0 && (
              <div className="text-sm text-[#647087]">No tickets yet — log one above.</div>
            )}
            {tickets.slice(0, 5).map((t) => (
              <button
                key={t.id}
                onClick={() => navigate(`/portal/tickets/${t.id}`)}
                className="block w-full text-left border border-white/5 rounded-md p-3 hover:border-rose-500/40 transition"
                data-testid={`portal-ticket-${t.id}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="font-medium truncate">{t.subject}</div>
                  <PhaseBadge value={t.status} />
                </div>
                {t.description && (
                  <div className="text-xs text-[#647087] truncate mt-0.5">{t.description}</div>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {openNew && <NewTicketModal onClose={() => setOpenNew(false)} onCreated={() => { setOpenNew(false); load(); }} />}
    </div>
  );
}

/* ====================== My Jobs ====================== */
function PortalJobs() {
  const [jobs, setJobs] = useState([]);
  const navigate = useNavigate();
  useEffect(() => {
    api.get("/jobcards").then((r) => setJobs(r.data));
  }, []);
  return (
    <div className="space-y-4">
      <h1 className="font-display text-3xl font-bold">My jobs</h1>
      <div className="hv-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#0a0b0e] border-b border-white/10">
            <tr className="text-left text-[#647087] text-xs uppercase tracking-wider">
              <th className="px-4 py-3">Title</th>
              <th className="px-4 py-3">Phase</th>
              <th className="px-4 py-3">Scheduled</th>
              <th className="px-4 py-3">Description</th>
            </tr>
          </thead>
          <tbody>
            {jobs.map((j) => (
              <tr
                key={j.id}
                onClick={() => navigate(`/portal/jobcards/${j.id}`)}
                className="border-b border-white/5 hover:bg-amber-500/5 transition cursor-pointer"
                data-testid={`portal-job-row-${j.id}`}
              >
                <td className="px-4 py-3 font-medium">{j.title}</td>
                <td className="px-4 py-3"><PhaseBadge value={j.phase} /></td>
                <td className="px-4 py-3 text-[#a0aabf] text-xs">
                  {j.scheduled_at ? new Date(j.scheduled_at).toLocaleString('en-GB') : "—"}
                </td>
                <td className="px-4 py-3 text-[#a0aabf] text-xs max-w-md truncate">{j.description}</td>
              </tr>
            ))}
            {jobs.length === 0 && (
              <tr>
                <td colSpan={4} className="px-4 py-8 text-center text-[#647087] text-sm">
                  No jobs yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ====================== My Tickets list ====================== */
function PortalTickets() {
  const [tickets, setTickets] = useState([]);
  const [openNew, setOpenNew] = useState(false);
  const navigate = useNavigate();
  const load = () => api.get("/tickets").then((r) => setTickets(r.data));
  useEffect(() => {
    load();
  }, []);
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-3xl font-bold">My tickets</h1>
        <button
          onClick={() => setOpenNew(true)}
          data-testid="portal-new-ticket-btn-list"
          className="hv-btn hv-btn-primary"
        >
          <Plus className="w-4 h-4" /> New ticket
        </button>
      </div>
      <div className="hv-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#0a0b0e] border-b border-white/10">
            <tr className="text-left text-[#647087] text-xs uppercase tracking-wider">
              <th className="px-4 py-3">Subject</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Updated</th>
            </tr>
          </thead>
          <tbody>
            {tickets.map((t) => (
              <tr
                key={t.id}
                onClick={() => navigate(`/portal/tickets/${t.id}`)}
                className="border-b border-white/5 hover:bg-rose-500/5 transition cursor-pointer"
                data-testid={`portal-ticket-row-${t.id}`}
              >
                <td className="px-4 py-3 font-medium">
                  {t.subject}
                  {(t.notes || []).length > 0 && (
                    <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-rose-500/10 border border-rose-500/30 text-rose-300">
                      {t.notes.length} notes
                    </span>
                  )}
                </td>
                <td className="px-4 py-3"><PhaseBadge value={t.status} /></td>
                <td className="px-4 py-3 text-[#a0aabf] text-xs">
                  {new Date(t.updated_at || t.created_at).toLocaleString('en-GB')}
                </td>
              </tr>
            ))}
            {tickets.length === 0 && (
              <tr>
                <td colSpan={3} className="px-4 py-8 text-center text-[#647087] text-sm">
                  No tickets yet — click <span className="text-[#1fb6c1]">New ticket</span> to log one.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {openNew && <NewTicketModal onClose={() => setOpenNew(false)} onCreated={() => { setOpenNew(false); load(); }} />}
    </div>
  );
}

/* ====================== Ticket detail ====================== */
function PortalTicketDetail() {
  const { tid } = useParams();
  const [ticket, setTicket] = useState(null);
  const [draft, setDraft] = useState("");
  const navigate = useNavigate();

  const load = async () => {
    const list = await api.get("/tickets");
    const t = list.data.find((x) => x.id === tid);
    setTicket(t);
  };
  useEffect(() => {
    load();
  }, [tid]);

  if (!ticket) return <div className="text-sm text-[#647087]">Loading…</div>;

  const send = async () => {
    if (!draft.trim()) return;
    await api.post(`/tickets/${tid}/notes`, { text: draft });
    setDraft("");
    load();
  };

  return (
    <div className="space-y-4">
      <button onClick={() => navigate(-1)} className="text-xs text-[#647087] hover:text-white">
        ← back
      </button>
      <div>
        <h1 className="font-display text-3xl font-bold">{ticket.subject}</h1>
        <div className="flex items-center gap-2 text-xs mt-2">
          <PhaseBadge value={ticket.status} />
          <span className="text-[#647087]">
            Logged {new Date(ticket.created_at).toLocaleString('en-GB')}
          </span>
        </div>
      </div>
      {ticket.description && (
        <div className="hv-card p-4 text-sm whitespace-pre-wrap leading-relaxed">
          {ticket.description}
        </div>
      )}
      {(ticket.attachments || []).length > 0 && (
        <div>
          <div className="text-xs uppercase tracking-wider text-[#647087] mb-2">Attachments</div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {ticket.attachments.map((a) => <AttachmentTile key={a.id} att={a} />)}
          </div>
        </div>
      )}
      <div>
        <div className="text-xs uppercase tracking-wider text-[#647087] mb-2">Conversation</div>
        <div className="space-y-2">
          {(ticket.notes || []).length === 0 && (
            <div className="text-sm text-[#647087]">No notes yet.</div>
          )}
          {(ticket.notes || []).map((n) => (
            <div
              key={n.id}
              className={`border rounded-md p-3 ${
                n.by_role === "client"
                  ? "border-[#1fb6c1]/30 bg-[#1fb6c1]/5"
                  : "border-white/10 bg-white/[0.02]"
              }`}
              data-testid={`portal-note-${n.id}`}
            >
              <div className="text-[10px] uppercase tracking-wider text-[#647087] mb-1 flex items-center gap-2">
                <span className="text-white">{n.by_user_name}</span>
                <span>{new Date(n.created_at).toLocaleString('en-GB')}</span>
                {n.by_role === "client" && (
                  <span className="text-[#1fb6c1]">· you</span>
                )}
              </div>
              <div className="text-sm whitespace-pre-wrap">{n.text}</div>
            </div>
          ))}
        </div>
      </div>
      <div className="hv-card p-3">
        <VoiceTextarea
          value={draft}
          onChange={setDraft}
          placeholder="Add a reply…"
          data-testid="portal-note-input"
         testIdPrefix="portal-note-input-voice" context="note" />
        <div className="flex justify-end mt-2">
          <button
            onClick={send}
            data-testid="portal-note-send"
            className="hv-btn hv-btn-primary"
            disabled={!draft.trim()}
          >
            <Send className="w-4 h-4" /> Send
          </button>
        </div>
      </div>
    </div>
  );
}

/* ====================== New Ticket Modal (with media) ====================== */
function NewTicketModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    subject: "",
    description: "",
    priority: "normal",
    attachments: [],
  });
  const [recording, setRecording] = useState(false);
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const onPickFile = async (fileOrFiles) => {
    if (!fileOrFiles) return;
    const list = fileOrFiles?.length !== undefined && typeof fileOrFiles !== "string"
      ? Array.from(fileOrFiles)
      : [fileOrFiles];
    if (list.length === 0) return;
    try {
      for (const file of list) {
        // eslint-disable-next-line no-await-in-loop
        const att = await uploadFile(file);
        setForm((f) => ({ ...f, attachments: [...f.attachments, att] }));
      }
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail, "Upload failed"));
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (ev) => chunksRef.current.push(ev.data);
      rec.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        const file = new File([blob], `voice-note-${Date.now()}.webm`, { type: "audio/webm" });
        stream.getTracks().forEach((t) => t.stop());
        await onPickFile(file);
        setRecording(false);
      };
      rec.start();
      recRef.current = rec;
      setRecording(true);
    } catch (e) {
      setErr("Microphone access denied.");
      setRecording(false);
    }
  };

  const stopRecording = () => {
    recRef.current?.stop();
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/tickets", {
        subject: form.subject,
        description: form.description,
        priority: form.priority,
        status: "open",
        attachments: form.attachments,
      });
      onCreated();
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail, "Could not log ticket"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open onClose={onClose} title="Log a new ticket" testId="portal-new-ticket-modal" maxWidth="max-w-xl">
      <form onSubmit={submit} className="space-y-3">
        <div>
          <label className="text-xs uppercase tracking-wider text-[#a0aabf]">Subject *</label>
          <input
            required
            value={form.subject}
            onChange={(e) => setForm({ ...form, subject: e.target.value })}
            className="hv-input mt-1"
            placeholder="e.g. Pump not working at main site"
            data-testid="portal-ticket-subject"
          />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-[#a0aabf]">Describe what's going on</label>
          <VoiceTextarea
            value={form.description}
            onChange={(v) => setForm({...form, description: v})}
            placeholder="When did it start? What does it look/sound like?"
            data-testid="portal-ticket-description"
           testIdPrefix="portal-ticket-description-voice" context="note" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-[#a0aabf]">Priority</label>
          <select
            value={form.priority}
            onChange={(e) => setForm({ ...form, priority: e.target.value })}
            className="hv-input mt-1"
          >
            <option value="low">Low</option>
            <option value="normal">Normal</option>
            <option value="high">High</option>
            <option value="urgent">Urgent</option>
          </select>
        </div>

        <div>
          <label className="text-xs uppercase tracking-wider text-[#a0aabf]">Attachments</label>
          <div className="grid grid-cols-3 gap-2 mt-2">
            <FilePickButton
              accept="image/*"
              onPick={onPickFile}
              icon={ImageIcon}
              label="Photo"
              testId="portal-ticket-photo"
            />
            <FilePickButton
              accept="video/*"
              onPick={onPickFile}
              icon={VideoIcon}
              label="Video"
              testId="portal-ticket-video"
            />
            <FilePickButton
              accept="*"
              onPick={onPickFile}
              icon={Paperclip}
              label="File"
              testId="portal-ticket-file"
            />
          </div>
          <button
            type="button"
            onClick={recording ? stopRecording : startRecording}
            className={`mt-2 w-full hv-btn ${
              recording ? "hv-btn-primary animate-pulse" : "hv-btn-ghost"
            }`}
            data-testid="portal-ticket-voice"
          >
            {recording ? (
              <>
                <Square className="w-4 h-4" /> Stop & attach voice note
              </>
            ) : (
              <>
                <Mic className="w-4 h-4" /> Record voice note
              </>
            )}
          </button>

          {form.attachments.length > 0 && (
            <div className="grid grid-cols-3 gap-2 mt-3">
              {form.attachments.map((a) => <AttachmentTile key={a.id} att={a} />)}
            </div>
          )}
        </div>
        {err && (
          <div className="text-xs text-rose-300 inline-flex items-center gap-1">
            <AlertOctagon className="w-3 h-3" /> {err}
          </div>
        )}
        <button
          type="submit"
          className="hv-btn hv-btn-primary w-full"
          disabled={saving}
          data-testid="portal-ticket-submit"
        >
          <Send className="w-4 h-4" /> {saving ? "Sending…" : "Submit ticket"}
        </button>
      </form>
    </Modal>
  );
}

function FilePickButton({ accept, onPick, icon: Icon, label, testId }) {
  const ref = useRef(null);
  return (
    <>
      <input
        ref={ref}
        type="file"
        accept={accept}
        multiple
        className="hidden"
        onChange={(e) => {
          onPick(e.target.files);
          e.target.value = "";
        }}
        data-testid={`${testId}-input`}
      />
      <button
        type="button"
        onClick={() => ref.current?.click()}
        className="hv-btn hv-btn-ghost flex-col gap-1 py-3"
        data-testid={testId}
      >
        <Icon className="w-4 h-4" />
        <span className="text-[10px]">{label}</span>
      </button>
    </>
  );
}

function AttachmentTile({ att }) {
  const url = uploadUrl(att.id);
  const ct = att.content_type || "";
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="block border border-white/10 rounded-md overflow-hidden hover:border-[#1fb6c1]/50 transition"
    >
      {ct.startsWith("image/") ? (
        <img src={url} alt={att.original_filename} className="w-full h-24 object-cover" />
      ) : ct.startsWith("video/") ? (
        <div className="h-24 flex items-center justify-center bg-black">
          <VideoIcon className="w-6 h-6 text-white/70" />
        </div>
      ) : ct.startsWith("audio/") ? (
        <div className="h-24 flex items-center justify-center bg-emerald-500/10">
          <Mic className="w-6 h-6 text-emerald-300" />
        </div>
      ) : (
        <div className="h-24 flex items-center justify-center">
          <Paperclip className="w-6 h-6 text-[#647087]" />
        </div>
      )}
      <div className="px-2 py-1 text-[10px] truncate text-[#a0aabf]">{att.original_filename}</div>
    </a>
  );
}

/* ====================== PhaseBadge (small util) ====================== */
function PhaseBadge({ value }) {
  if (!value) return null;
  const map = {
    open: "#F59E0B",
    in_progress: "#3B82F6",
    resolved: "#10B981",
    closed: "#647087",
    picking: "#A78BFA",
    scheduled: "#3B82F6",
    complete: "#10B981",
    signed_off: "#22C55E",
    paid: "#1FB6C1",
  };
  const c = map[value] || "#A78BFA";
  return (
    <span
      className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full font-bold"
      style={{ background: `${c}1a`, color: c, border: `1px solid ${c}55` }}
    >
      {value.replace("_", " ")}
    </span>
  );
}

/* ====================== Router entry (legacy) ====================== */
export default function ClientPortal() {
  return (
    <Routes>
      <Route index element={<PortalOverview />} />
      <Route path="jobs" element={
        <PortalRouteGuard perm="portal.jobs.read"><PortalJobs /></PortalRouteGuard>
      } />
      <Route path="tickets" element={
        <PortalRouteGuard perm="portal.tickets.read"><PortalTickets /></PortalRouteGuard>
      } />
      <Route path="tickets/:tid" element={
        <PortalRouteGuard perm="portal.tickets.read"><PortalTicketDetail /></PortalRouteGuard>
      } />
      <Route path="documents" element={
        <PortalRouteGuard perm="portal.documents.read"><PortalDocuments /></PortalRouteGuard>
      } />
      <Route path="performance" element={
        <PortalRouteGuard perm="portal.performance.read"><PortalPerformance /></PortalRouteGuard>
      } />
      <Route path="team" element={
        <PortalRouteGuard perm="portal.users.manage"><PortalTeamManager /></PortalRouteGuard>
      } />
    </Routes>
  );
}

function PortalDocuments() {
  const { branding } = useBranding();
  const BRAND_NAME = branding?.app_name || "Rogue";
  return (
    <div className="space-y-4" data-testid="portal-documents-page">
      <div>
        <div className="text-xs uppercase tracking-[0.25em] text-[#647087]">{BRAND_NAME} Portal</div>
        <h1 className="font-display text-2xl md:text-3xl font-bold tracking-tight mt-1">Documents</h1>
        <p className="text-sm text-[#a0aabf] mt-1">Contracts, proposals, manuals, warranties, account docs, notices and more — all your documents in one place.</p>
      </div>
      <CustomerProfileDocumentsBlock apiBase="/portal" canEdit={false} canDelete={false} />
    </div>
  );
}

export function PortalPerformance() {
  const { branding } = useBranding();
  const { user } = useAuth();
  const navigate = useNavigate();
  const BRAND_NAME = branding?.app_name || "Rogue";
  return (
    <div className="space-y-4" data-testid="portal-performance-page">
      {/* Iter 392 — Back button so users can escape the full-screen
          performance page without hunting for browser back. */}
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="hv-btn hv-btn-ghost text-xs flex items-center gap-1.5"
        data-testid="portal-performance-back"
      >
        <ArrowLeft className="w-3.5 h-3.5" /> Back
      </button>
      <div>
        <div className="text-xs uppercase tracking-[0.25em] text-[#647087]">{BRAND_NAME} Portal</div>
        <h1 className="font-display text-2xl md:text-3xl font-bold tracking-tight mt-1">Performance</h1>
        <p className="text-sm text-[#a0aabf] mt-1">Daily readings, target vs actual, and equipment history for your sites.</p>
      </div>
      <PerformanceBlock
        customerId={user?.customer_id}
        canEdit={false}
        canCapture
        defaultOpen
        isPortal
      />
    </div>
  );
}

function Kpi({ label, value, accent, icon: Icon }) {
  return (
    <div
      className="hv-card p-4 flex items-center justify-between"
      style={{ borderColor: `${accent}40`, background: `${accent}08` }}
    >
      <div>
        <div className="text-xs uppercase tracking-wider text-[#a0aabf]">{label}</div>
        <div className="font-display text-3xl font-bold mt-0.5" style={{ color: accent }}>
          {value}
        </div>
      </div>
      <div
        className="w-10 h-10 rounded-md flex items-center justify-center"
        style={{ background: `${accent}1a`, color: accent, border: `1px solid ${accent}55` }}
      >
        <Icon className="w-5 h-5" />
      </div>
    </div>
  );
}
